import CampaignAPI from '@/api/campaign.api.js'
import SoftphoneAPI, { SOFTPHONE_STATE_CHANGE_REASON } from '@/api/softphone.api'
import { currencyFormatter } from '@/utils/formatCurrency'
import cloneDeep from 'lodash.clonedeep'

// Its own copy rather than an import from creative.store, which pulls the whole store graph in
// behind it - auth.store.js keeps one for the same reason.
const MEDICARE_FLAG = 'MEDICARE'

export const PAUSED_STATUS = 'Paused'
export const ACTIVE_STATUS = 'Active'
export const RTB_ID_REGEX = /^[a-z0-9]{32}$/

export const campaignRoutingTypes = {
  Static: 'Static',
  RingbaRTB: 'RingbaRTB',
  Programmatic: 'Programmatic'
}

export const destinationTypes = {
  Number: 'Number',
  SIP: 'SIP',
  Softphone: 'Softphone'
}

// Softphone campaigns are always US; the destination is provisioned per agent rather than
// entered, so the buyer is never asked for a country.
export const SOFTPHONE_CAMPAIGN_COUNTRY_ID = 'US'

// Preferred when the category offers a choice - the buyer can still pick another.
export const SOFTPHONE_CAMPAIGN_PREFERRED_LANGUAGE_ID = 'en'

// Used when neither the account nor the browser names a US time zone. The campaign's time zone is
// what its daily and hourly budget windows are measured in, so it has to be something.
export const SOFTPHONE_CAMPAIGN_FALLBACK_TIME_ZONE_ID = 'Eastern Standard Time'

// The account time zone is set on the Account > Timezone page and can simply be unset, but the
// softphone create page has no Schedule section to ask on. The browser's zone is the next best
// guess at where the agent is; these are the Windows ids the API and the timezone list use, for
// the US zones a US-only campaign can be in.
const US_TIME_ZONE_IDS_BY_BROWSER_ZONE = Object.freeze({
  'America/New_York': 'Eastern Standard Time',
  'America/Detroit': 'Eastern Standard Time',
  'America/Indiana/Indianapolis': 'Eastern Standard Time',
  'America/Kentucky/Louisville': 'Eastern Standard Time',
  'America/Chicago': 'Central Standard Time',
  'America/Indiana/Knox': 'Central Standard Time',
  'America/Menominee': 'Central Standard Time',
  'America/Denver': 'Mountain Standard Time',
  'America/Boise': 'Mountain Standard Time',
  'America/Phoenix': 'US Mountain Standard Time',
  'America/Los_Angeles': 'Pacific Standard Time',
  'America/Anchorage': 'Alaskan Standard Time',
  'America/Juneau': 'Alaskan Standard Time',
  'America/Nome': 'Alaskan Standard Time',
  'Pacific/Honolulu': 'Hawaiian Standard Time'
})

// The account's own time zone always wins - it is what the rest of the buyer's campaigns and
// reports use, so a softphone campaign must not quietly disagree with it.
export function resolveSoftphoneCampaignTimeZoneId(accountTimeZoneId, browserTimeZone) {
  if (accountTimeZoneId) {
    return accountTimeZoneId
  }

  return US_TIME_ZONE_IDS_BY_BROWSER_ZONE[browserTimeZone] ?? SOFTPHONE_CAMPAIGN_FALLBACK_TIME_ZONE_ID
}

// What the browser thinks the agent's time zone is, as an IANA name. Guarded: Intl is everywhere
// this app runs, but a resolved zone is not worth throwing a page away for.
export function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    return null
  }
}

// Which categories a softphone campaign may be created for. Two are left out, both because the
// page cannot show what the category implies:
//   - CPA-only bills on postback conversions and hides Minimum Duration, and this page has no
//     other place to put those terms.
//   - Medicare routes on approved creatives instead of traffic sources, and a softphone campaign
//     has no creative flow at all - the campaign would be enabled but reachable by no seller.
export function isSoftphoneEligibleCategory(category) {
  return Boolean(category)
    && category.isCpaConversionOnly !== true
    && !(category.flags ?? []).includes(MEDICARE_FLAG)
}

// Only an approved campaign can take calls: it is the approval that creates the Ringba target the
// calls arrive through. A new campaign is InReview and a rejected one never will be, and neither
// is paused - so without this check going live on one is an idempotent no-op that leaves the agent
// waiting in silence. An allow-list, so Expired and Deleted are refused too.
export function canSoftphoneCampaignGoLive(campaign) {
  return campaign?.status === ACTIVE_STATUS || campaign?.status === PAUSED_STATUS
}

// Shown when the agent already holds the campaign they are trying to create again. Worded like
// the API's own refusal, because either one can be the first thing the agent sees: this one on
// submit, the API's if a campaign was created elsewhere after this page loaded.
export const SOFTPHONE_DUPLICATE_CAMPAIGN_MESSAGE =
  'You already have a softphone campaign for this category and language. Use that campaign instead of creating another one.'

// Appended to a category or language the agent has already used, next to disabling it.
export const SOFTPHONE_COMBO_TAKEN_SUFFIX = '(already have a campaign)'

// A softphone campaign's destination is the single agent it rings - the one who created it - so
// the API allows one campaign per agent per category/country/language. This is the key that
// rule turns on, mirroring the destination id the API builds server-side.
function buildComboKey(categoryId, countryId, languageId) {
  return `${categoryId ?? ''}|${countryId ?? ''}|${languageId ?? ''}`
}

// The page only ever creates US campaigns, so a combination it can reach is a US one.
export function buildSoftphoneCampaignComboKey(categoryId, languageId) {
  return buildComboKey(categoryId, SOFTPHONE_CAMPAIGN_COUNTRY_ID, languageId)
}

// The campaigns list covers the whole account, including the other agents' softphone campaigns.
// Those do not constrain this agent - each agent gets their own campaign per combination - so
// only the ones this agent created are taken. Each campaign's own country goes into the key
// rather than an assumed US: a campaign for another country blocks nothing this page can create.
export function getTakenSoftphoneCampaignCombos(campaigns, agentUserId) {
  if (!agentUserId) {
    return []
  }

  return (campaigns ?? [])
    .filter(campaign => isSoftphoneCampaign(campaign) && campaign.createdBy === agentUserId)
    .map(campaign => buildComboKey(campaign.category?.id, campaign.country?.id, campaign.language?.id))
}

export function isSoftphoneCampaignComboTaken(takenCombos, categoryId, languageId) {
  return (takenCombos ?? []).includes(buildSoftphoneCampaignComboKey(categoryId, languageId))
}

export function getTakenSoftphoneLanguageIds(takenCombos, categoryId, categoryLanguages) {
  return (categoryLanguages ?? [])
    .filter(language => isSoftphoneCampaignComboTaken(takenCombos, categoryId, language.id))
    .map(language => language.id)
}

// A category is out only once every language it offers is taken - with one language left the
// agent can still create a campaign there. A category with no configured languages is left
// alone: there is no combination to have taken.
export function isSoftphoneCategoryTaken(takenCombos, category) {
  const languages = category?.languages ?? []

  return languages.length > 0
    && languages.every(language => isSoftphoneCampaignComboTaken(takenCombos, category.id, language.id))
}

// A category with one configured language leaves nothing to choose, so take it. With several,
// keep whatever is already selected if the new category still supports it, else fall back to
// English, else make the buyer pick (the API rejects a language the category is not configured
// for, so guessing is worse than an empty required field). Languages the agent has already used
// are dropped first - preselecting one would put the form in a state the API refuses.
export function resolveSoftphoneCampaignLanguageId(categoryLanguages, currentLanguageId, takenLanguageIds = []) {
  const languages = (categoryLanguages ?? []).filter(language => !takenLanguageIds.includes(language.id))

  if (languages.length === 1) {
    return languages[0].id
  }

  const supports = languageId => languages.some(language => language.id === languageId)

  if (currentLanguageId && supports(currentLanguageId)) {
    return currentLanguageId
  }

  return supports(SOFTPHONE_CAMPAIGN_PREFERRED_LANGUAGE_ID) ? SOFTPHONE_CAMPAIGN_PREFERRED_LANGUAGE_ID : null
}

export function isSoftphoneCampaign(campaign) {
  return campaign?.routing?.type === campaignRoutingTypes.Static
    && campaign?.routing?.destinationType === destinationTypes.Softphone
}

// The softphone creation page hides Basic Details, so the required campaign name is derived
// from the two things that identify it to the buyer: the category and who set it up.
export function buildSoftphoneCampaignName(categoryName, userName) {
  return ['Softphone', categoryName, userName].filter(part => Boolean(part)).join(' ')
}

// The Routing column shows what the buyer picked, not the internal routing type.
export function getCampaignRoutingLabel(campaign, startCase) {
  return isSoftphoneCampaign(campaign) ? 'Softphone' : startCase(campaign?.routing?.type)
}

export function getDefaultParser() {
  return {
    parserType: 'jpath'
  }
}

export function convertCurrencyStringToNumber(currencyString) {
  if (currencyString == null) {
    return currencyString
  }
  
  currencyString = currencyString.toString()
  return Number(currencyString.replace(/[$,]/g, ''))
}

export function getBadgeVariantForCampaignStatus(campaign) {
  if (!campaign) {
    return 'default'
  }

  if (campaign.isPaused) {
    // A wrap-up pause is transient and the agent is still live - not the same yellow as a real pause.
    return campaign.isWrapUpPause ? 'info' : 'warning'
  }

  switch (campaign.status) {
  case 'InReview':
    return 'info'
  case 'Active':
    return 'success'
  case 'Paused':
    return 'warning'
  case 'Rejected':
    return 'danger'
  default:
    return 'default'
  }
}

export function getStatusText(campaign) {
  if (!campaign) {
    return ''
  }

  if (campaign.isPaused) {
    return campaign.isWrapUpPause ? 'Paused (wrap-up)' : 'Paused'
  }

  if (campaign.status === 'InReview') {
    return 'In Review'
  }

  return campaign.status
}


export function getRtbTagsWithDescription() {
  const rtbTags = [{
    key: "trafficsource",
    value: "trafficsource",
    description: "Traffic Source"
  },
  {
    key: "thirdparty",
    value: "thirdparty",
    description: "Third Party"
  },
  {
    key: "age",
    value: "age",
    description: "Age"
  },
  {
    key: "homeowner",
    value: "homeowner",
    description: "Homeowner"
  },
  {
    key: "state",
    value: "state",
    description: "US state"
  },
  {
    key: "zipcode",
    value: "zipcode",
    description: "ZIP code"
  },  
  {
    key: "creative",
    value: "creative",
    description: "Creative"
  },
  {
    key: "channel",
    value: "channel",
    description: "Channel Name"
  }]

  const tagDescriptionMapping = new Map(rtbTags.map(tag => [tag.key, tag.description]))

  return { rtbTags, tagDescriptionMapping }
}

export function transformBuyerCampaignForAPI(buyerCampaign) {
  const tempCampaignClone = cloneDeep(buyerCampaign)

  tempCampaignClone.audience = {
    ...tempCampaignClone.audience
  }

  tempCampaignClone.budget = {
    ...tempCampaignClone.budget,
    dailyBudgetInUSD: convertCurrencyStringToNumber(tempCampaignClone.budget.dailyBudgetInUSD),
    hourlyBudgetInUSD: tempCampaignClone.budget.hourlyBudgetInUSD
      ? convertCurrencyStringToNumber(tempCampaignClone.budget.hourlyBudgetInUSD) : 0,
    // maxBidPerCallInUSD is required by the backend and must be in range 1.0-9999.99
    // It will default to 1 only if the field is not visible in UI and validation does not force user to enter the value, like for RTB campaigns
    maxBidPerCallInUSD: convertCurrencyStringToNumber(tempCampaignClone.budget.maxBidPerCallInUSD),
    isPostbackConversion: tempCampaignClone.budget.isPostbackConversion || false
  }

  if (tempCampaignClone.routing.destinationType === destinationTypes.Softphone) {
    // Provisioned per agent - the buyer supplies neither a number nor SIP details.
    tempCampaignClone.routing.number = ''
    tempCampaignClone.routing.sipNumber = {}
  } else if (tempCampaignClone.routing.destinationType === destinationTypes.SIP) {
    tempCampaignClone.routing.number = ''
  } else {
    tempCampaignClone.routing.sipNumber = {}
  }

  if (tempCampaignClone.routing.type === 'Programmatic') {
    const tempProgrammaticTarget = {
      ...tempCampaignClone.routing.programmaticTarget
    }

    const transformedHeaders = tranformHeaders(tempProgrammaticTarget.headers)

    if (!tempProgrammaticTarget.bidSettings.isBidDynamic) {
      delete tempProgrammaticTarget.dynamicBidAmountParser
    }

    if (!tempProgrammaticTarget.bidSettings.conversionCallLengthDynamic) {
      delete tempProgrammaticTarget.dynamicCallLengthSettingParser
    }

    if (!tempProgrammaticTarget.isNumberDynamic) {
      delete tempProgrammaticTarget.dynamicDialSettingParser
    }

    tempProgrammaticTarget.bidSettings = {
      ...tempProgrammaticTarget.bidSettings,
      staticBidAmount: convertCurrencyStringToNumber(tempProgrammaticTarget.bidSettings.staticBidAmount),
      bidOnFailure: convertCurrencyStringToNumber(tempProgrammaticTarget.bidSettings.bidOnFailure)
    }

    tempProgrammaticTarget.headers = transformedHeaders

    tempCampaignClone.routing = {
      ...tempCampaignClone.routing,
      programmaticTarget: tempProgrammaticTarget
    }
  } else {
    const tempRingbaRTBTarget = {
      ...tempCampaignClone.routing.ringbaRTB
    }

    let transformedTagMappings = null

    if (tempRingbaRTBTarget.tagMappings?.length) {
      transformedTagMappings = tranformHeaders(tempRingbaRTBTarget.tagMappings)
    }

    tempRingbaRTBTarget.tagMappings = transformedTagMappings
    tempRingbaRTBTarget.defaultPostbackBidAmount = convertCurrencyStringToNumber(tempRingbaRTBTarget.defaultPostbackBidAmount)

    tempCampaignClone.routing = {
      ...tempCampaignClone.routing,
      programmaticTarget: null,
      ringbaRTB: tempRingbaRTBTarget
    }
  }

  if (tempCampaignClone.routing.type !== 'RingbaRTB') {
    tempCampaignClone.routing = {
      ...tempCampaignClone.routing,
      ringbaRTB: null
    }
  }

  tempCampaignClone.trafficSources = {
    ...tempCampaignClone.trafficSources,
    allowedSources: tempCampaignClone.trafficSources.allowedSources.map(id => {
      return {
        id
      }
    })
  }

  return tempCampaignClone
}

function tranformHeaders(headers) {
  let tempHeaders = {}

  headers?.forEach(header => {
    tempHeaders[header.key] = header.value
  })

  return tempHeaders
}


function transformHeadersFromAPIToUI(headers) {
  return  headers
    ? Object.entries(headers).map(entry => ({
      key: entry[0],
      value: entry[1]
    }))
    : []

}

function getDefaultProgrammaticTarget() {
  return {
    bidSettings: {
      staticBidAmount: '$1',
      bidOnFailure: '$0.5',
      bidType: 'Static',
      isBidDynamic: false,
      conversionCallLengthDynamic: false,
      conversionCallLengthInSeconds: 1,
      maxConversionCallDurationSeconds: 1,
    },
    destinationType: 'Number',
    number: '',
    sipNumber: {},
    method: null,
    isNumberDynamic: false,
    headers: [],

    dynamicBidAmountParser: [getDefaultParser()],
    shouldAcceptCallParser: [getDefaultParser()],
    dynamicDialSettingParser: [getDefaultParser()],
    dynamicCallLengthSettingParser: [getDefaultParser()],
  }
}

function getDefaultBuyerCampaign() {
  return {
    categoryId: null,
    countryId: null,
    languageId: null,
    name: '',
    routing: {
      type: 'Static',
      destinationType: 'Number',
      number: '+1',
      sipNumber: {},
      programmaticTarget: getDefaultProgrammaticTarget(),
      ringbaRTB: {},

      schedule: {
        hoursOfOperation: null,
        timeZoneId: null,
        concurrencyCap: 1,
        hourlyConcurrencyCap: null
      },
    },
    trafficSources: {
      callType: 'Inbound',
      requirements: 'None',
      allowThirdParties: true,
      allowTollFreeCallers: true,
      restrictDuplicates: 'InheritByBuyer',
      allowedSources: []
    },
    audience: {      
      searchLocation: false,
      selectedLocations: [],
      locationFilterType: 'States',
      noIcp: true, 
      phoneType: '',    
    },

    // integrations: {},

    budget: {
      minimumDurationSelected: true,
      minimumDurationInSeconds: 90,
      hourlyBudgetSelected: false,
      isPostbackConversion: false
      //Do not "suggest" any monetary values here
      // dailyBudgetInUSD: '$10',
      // maxBidPerCallInUSD: '$1'
    }
  }
}

export function transformBuyerCampaignFromAPIForUI(campaign) {
  let transformedCampaign = {...campaign}

  if (!transformedCampaign.routing.programmaticTarget) {
    transformedCampaign.routing.programmaticTarget = getDefaultProgrammaticTarget()
  }

  if (transformedCampaign.routing.type === 'Programmatic') {
    transformedCampaign.routing.programmaticTarget.headers = transformHeadersFromAPIToUI(transformedCampaign.routing.programmaticTarget.headers)
  }

  if (transformedCampaign.routing.type === 'RingbaRTB') {
    transformedCampaign.routing.ringbaRTB.tagMappings = transformHeadersFromAPIToUI(transformedCampaign.routing.ringbaRTB.tagMappings)
    if (transformedCampaign.routing.ringbaRTB.defaultPostbackBidAmount != null) {
      transformedCampaign.routing.ringbaRTB.defaultPostbackBidAmount = currencyFormatter(transformedCampaign.routing.ringbaRTB.defaultPostbackBidAmount)
    }
  }

  const programmaticTarget = transformedCampaign.routing.programmaticTarget

  if (!programmaticTarget.dynamicBidAmountParser) {
    programmaticTarget.dynamicBidAmountParser = [getDefaultParser()]
  }

  if (!programmaticTarget.shouldAcceptCallParser) {
    programmaticTarget.shouldAcceptCallParser = [getDefaultParser()]
  }

  if (!programmaticTarget.dynamicDialSettingParser) {
    programmaticTarget.dynamicDialSettingParser = [getDefaultParser()]
  }

  if (!programmaticTarget.dynamicCallLengthSettingParser) {
    programmaticTarget.dynamicCallLengthSettingParser = [getDefaultParser()]
  }

  programmaticTarget.bidSettings.bidType = programmaticTarget.bidSettings.isBidDynamic ? 'Dynamic' : 'Static'
  
  if (!programmaticTarget.bidSettings.isBidDynamic) {
    programmaticTarget.bidSettings.staticBidAmount = currencyFormatter(programmaticTarget.bidSettings.staticBidAmount)
    programmaticTarget.bidSettings.bidOnFailure = currencyFormatter(programmaticTarget.bidSettings.bidOnFailure)
  }

  transformedCampaign.routing.ringbaRTB =  {
    ...campaign.routing.ringbaRTB
  }

  transformedCampaign.categoryId = campaign.category.id
  transformedCampaign.countryId = campaign.country.id
  transformedCampaign.languageId = campaign.language.id

  transformedCampaign.trafficSources.allowedSources = transformedCampaign.trafficSources.allowedSources.map(ts => ts.id)

  transformedCampaign.budget.dailyBudgetInUSD = currencyFormatter(transformedCampaign.budget.dailyBudgetInUSD)
  transformedCampaign.budget.maxBidPerCallInUSD = currencyFormatter(transformedCampaign.budget.maxBidPerCallInUSD)
  transformedCampaign.budget.isPostbackConversion = campaign.budget.isPostbackConversion || false

  if (transformedCampaign.budget.hourlyBudgetSelected) {
    transformedCampaign.budget.hourlyBudgetInUSD = currencyFormatter(transformedCampaign.budget.hourlyBudgetInUSD)
  }

  transformedCampaign.audience.searchLocation = campaign.audience.searchLocation
  transformedCampaign.audience.selectedLocations = campaign.audience.selectedLocations
  
  return transformedCampaign
}

const state = {
  buyerCampaigns: [],
  buyerCampaign: getDefaultBuyerCampaign()
}

const getters = {
  isProgrammaticBuyerCampaign(state) {
    return state.buyerCampaign?.routing?.type === campaignRoutingTypes.Programmatic
  },

  isRingbaRTBBuyerCampaign(state) {
    return state.buyerCampaign?.routing?.type === campaignRoutingTypes.RingbaRTB
  }
}

const actions = {
  async createBuyerCampaign({ rootGetters }, campaign) {
    const response = await CampaignAPI.createBuyerCampaign({
      accountId: rootGetters['auth/accountDetails']?.id,
      campaign: transformBuyerCampaignForAPI(campaign)
    })

    return response.data
  },

  async fetchBuyerCampaigns({ commit, rootGetters }) {
    const response = await CampaignAPI.listBuyerCampaigns(
      rootGetters['auth/accountDetails']?.id,
    )

    commit('SET_BUYER_CAMPAIGNS', response.data)
  },

  async fetchBuyerCampaign({ commit, rootGetters, dispatch }, id) {
    const response = await CampaignAPI.getBuyerCampaign(
      rootGetters['auth/accountDetails']?.id,
      id
    )

    commit('SET_BUYER_CAMPAIGN', transformBuyerCampaignFromAPIForUI(response.data))

    if (response.data.isDailyBudgetReached) {
      dispatch('showDashboardNotification', {
        text: 'Your campaign daily budget is exceeded. ',      
        hideOnClick: false
      }, { root:true })
    }
  },

  async pauseBuyerCampaign({ commit, rootGetters }, { campaignId }) {
    const response = await CampaignAPI.pauseBuyerCampaign({
      accountId: rootGetters['auth/accountDetails']?.id,
      campaignId
    })

    commit('UPDATE_BUYER_CAMPAIGN_STATUS_IN_LIST', { campaignId, isPaused: true })

    return response.data
  },

  async resumeBuyerCampaign({ commit, rootGetters }, { campaignId, status }) {
    const response = await CampaignAPI.resumeBuyerCampaign({
      accountId: rootGetters['auth/accountDetails']?.id,
      campaignId
    })

    commit('UPDATE_BUYER_CAMPAIGN_STATUS_IN_LIST', {
      campaignId,
      isPaused: false,
      status: status === PAUSED_STATUS ? ACTIVE_STATUS : status
    })

    return response.data
  },

  /**
   * Softphone Go Live. One server call pauses the agent's other live softphone campaigns and resumes
   * this one. The selected row is updated in place so the button flips immediately; the rest of the
   * list is refetched because which OTHER campaigns the server paused is only known there.
   */
  async goLiveSoftphoneCampaign({ commit, dispatch, rootGetters }, { campaignId, status }) {
    const response = await SoftphoneAPI.goLive(rootGetters['auth/accountDetails']?.id, campaignId)

    commit('UPDATE_BUYER_CAMPAIGN_STATUS_IN_LIST', {
      campaignId,
      isPaused: false,
      status: status === PAUSED_STATUS ? ACTIVE_STATUS : status
    })

    await dispatch('refreshBuyerCampaignsIfLoaded')

    return response.data
  },

  /**
   * Softphone Go Offline. The server pauses every live softphone campaign ringing the agent, so this
   * takes no campaign id. Dispatched from the widget window too, where no list is loaded - hence the
   * conditional refresh rather than an unconditional fetch.
   */
  async goOfflineSoftphone({ dispatch, rootGetters }) {
    const response = await SoftphoneAPI.goOffline(rootGetters['auth/accountDetails']?.id)

    await dispatch('refreshBuyerCampaignsIfLoaded')

    return response.data
  },

  /**
   * Post-call wrap-up, pause half. Dispatched by the widget once an answered call has run long enough
   * that the agent will need a breather after it. Same server call as Go Offline - every softphone
   * campaign ringing the agent is paused - with a reason so the campaign's audit note says wrap-up
   * rather than that the agent walked away.
   */
  async pauseSoftphoneForWrapUp({ dispatch, rootGetters }) {
    const response = await SoftphoneAPI.goOffline(rootGetters['auth/accountDetails']?.id, {
      reason: SOFTPHONE_STATE_CHANGE_REASON.WRAP_UP
    })

    await dispatch('refreshBuyerCampaignsIfLoaded')

    return response.data
  },

  /**
   * Post-call wrap-up, resume half. Same server call as Go Live for the campaign the agent was live
   * on. No local list mutation: this runs in the widget window, which has no list, and the dashboard
   * tabs refetch when the widget's broadcast tells them the wrap-up pause lifted.
   */
  async resumeSoftphoneAfterWrapUp({ dispatch, rootGetters }, { campaignId }) {
    const response = await SoftphoneAPI.goLive(rootGetters['auth/accountDetails']?.id, campaignId, {
      reason: SOFTPHONE_STATE_CHANGE_REASON.WRAP_UP
    })

    await dispatch('refreshBuyerCampaignsIfLoaded')

    return response.data
  },

  /**
   * Best effort: a stale list is a cosmetic problem, a failed go-live/go-offline is not, so a refetch
   * error must not fail the caller.
   */
  async refreshBuyerCampaignsIfLoaded({ state, dispatch }) {
    if (!state.buyerCampaigns?.length) {
      return
    }

    try {
      await dispatch('fetchBuyerCampaigns')
    } catch (error) {
      console.error('Failed to refresh buyer campaigns after a softphone state change', error)
    }
  },

  // Resume a buyer campaign for an explicitly provided account, used when the
  // user arrives from an email link where the current account context may not
  // match the campaign's account.
  async resumeBuyerCampaignByAccount(context, { accountId, campaignId }) {
    const response = await CampaignAPI.resumeBuyerCampaign({ accountId, campaignId })

    return response.data
  },

  async pauseSellerCampaign(context, { campaignId }) {
    const response = await CampaignAPI.pauseSellerCampaign({
      campaignId
    })
    return response.data
  },

  async resumeSellerCampaign(context, { campaignId }) {
    const response = await CampaignAPI.resumeSellerCampaign({
      campaignId
    })
    return response.data
  },

  async updateCampaignBid(context, { campaignId, bidValue }) {
    const response = await CampaignAPI.updateCampaignBid({
      campaignId,
      bidValue
    })
    return response.data
  },

  async editBuyerCampaign({ rootGetters }, campaign) {
    campaign.country = campaign.countryId
    campaign.category = campaign.categoryId
    campaign.language = campaign.languageId    

    if (!campaign.audience.wirelessCarrierSelected) {
      campaign.audience.wirelessCarriers = []
    }
    if (!campaign.audience.lineTypeSelected) {
      campaign.audience.lineTypes = []
    }

    const response = await CampaignAPI.editBuyerCampaign({
      accountId: rootGetters['auth/accountDetails']?.id,
      campaign: transformBuyerCampaignForAPI(campaign)
    })

    return response.data
  },

  updateDynamicTargetInBuyerCampaign({ commit }, target) {
    commit('UPDATE_BUYER_CAMPAIGN_DYNAMIC_TARGET', target)
  },

  setDefaultBuyerCampaign({ commit }) {
    commit('SET_DEFAULT_BUYER_CAMPAIGN')
  },

  async uploadZipCodesFile({ rootGetters }, file) {    
    const response = await CampaignAPI.uploadZipCodesFile({
      accountId: rootGetters['auth/accountDetails']?.id,
      file: file
    })
    
    return response.data
  }
}

const mutations = {
  SET_DEFAULT_BUYER_CAMPAIGN(state) {
    state.buyerCampaign = getDefaultBuyerCampaign()
  },

  SET_BUYER_CAMPAIGN(state, campaign) {
    state.buyerCampaign = campaign
  },

  SET_BUYER_CAMPAIGNS(state, campaigns) {
    state.buyerCampaigns = campaigns
  },

  UPDATE_BUYER_CAMPAIGN_STATUS_IN_LIST(state, { campaignId, status, isPaused }) {
    const buyerCampaignIndex = state.buyerCampaigns.findIndex(c => c.id === campaignId)

    if (status) {
      state.buyerCampaigns[buyerCampaignIndex].status = status
    }
    
    state.buyerCampaigns[buyerCampaignIndex].isPaused = isPaused

    state.buyerCampaigns = [...state.buyerCampaigns]
  },

  UPDATE_BUYER_CAMPAIGN_BASIC_DETAILS(state, { name, categoryId, countryId, languageId }) {
    state.buyerCampaign = {
      ...state.buyerCampaign,
      name,
      categoryId,
      countryId,
      languageId
    }
  },
  UPDATE_BUYER_CAMPAIGN_ROUTING(state, routing) {
    state.buyerCampaign.routing = routing
  },
  UPDATE_BUYER_CAMPAIGN_DYNAMIC_TARGET(state, programmaticTarget) {
    state.buyerCampaign.routing.programmaticTarget = programmaticTarget
  },
  UPDATE_BUYER_CAMPAIGN_TRAFFIC_SOURCES(state, trafficSources) {
    state.buyerCampaign.trafficSources = trafficSources
  },
  UPDATE_BUYER_CAMPAIGN_AUDIENCE(state, audience) {
    state.buyerCampaign.audience = audience
  },
  UPDATE_BUYER_CAMPAIGN_INTEGRATIONS(state, integrations) {
    state.buyerCampaign.integrations = integrations
  },
  UPDATE_BUYER_CAMPAIGN_BUDGET(state, budget) {
    state.buyerCampaign.budget = budget
  },
  CLEAR_BUYER_CAMPAIGN_ZIP_CODES_FILE_LINK(state) {
    state.buyerCampaign.zipCodesFileLink = null
  },

  RESET_BUYER_CAMPAIGN_MINIMUM_DURATION(state) {
    state.buyerCampaign.budget = {
      ...state.buyerCampaign.budget,
      minimumDurationInSeconds: 1,
      minimumDurationSelected: false,
      hourlyBudgetSelected: false
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
